import { logger } from "../services/logger.ts";
import React, { useState, useEffect } from 'react';
import { Search, UserPlus, ShieldOff, Settings, ArrowLeft, Ban, Filter, ChevronLeft, ChevronRight, Download, Mail, CheckCircle, Key, BarChart3, DollarSign, Calendar } from 'lucide-react';
import { useAuth } from "../lib/auth.tsx";
import { supabase } from "../lib/db.ts";
import { Link, useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { AddPartnerModal } from '../components/AddPartnerModal';
import { PartnerApiKeysModal } from '../components/PartnerApiKeysModal';
import { PartnerManagementModal } from '../components/PartnerManagementModal';

interface Partner {
  id: string;
  email: string;
  role: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_blocked: boolean;
  fica_verified: boolean;
  phone: string | null;
  is_sso_user: boolean;
  is_anonymous: boolean;
  wallet_balance: number | null;
  email_confirmed_at: string | null;
  // Partner-specific fields
  partner_name?: string;
  commission_rate?: number;
  allowed_categories?: string[];
  max_markets_per_day?: number;
  markets_created_today?: number;
  total_markets_created?: number;
  last_api_call?: string | null;
}

export function AdminPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPartners, setTotalPartners] = useState(0);
  const pageSize = 10;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showApiKeysModal, setShowApiKeysModal] = useState<{ partnerName: string; partnerId: string } | null>(null);
  const [showPartnerManagementModal, setShowPartnerManagementModal] = useState<Partner | null>(null);

  // Check if user is admin
  useEffect(() => {
    if (!user || (user.app_metadata?.role !== 'admin' && user.app_metadata?.role !== 'superuser')) {
      navigate('/markets');
    }
  }, [user, navigate]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterStatus]);

  const fetchPartners = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearching(true);
      }
      
      logger.debug('Fetching partners with params:', {
        search: debouncedSearchTerm,
        filter: filterStatus,
        page: currentPage,
        pageSize
      });

      // First, get all users and filter for partners
      const { data, error: usersError } = await supabase
        .rpc('admin_list_users_paginated', {
          p_search_term: debouncedSearchTerm,
          p_filter_blocked: filterStatus === 'blocked' ? 'blocked' : filterStatus === 'active' ? 'active' : 'all',
          p_page: currentPage,
          p_page_size: pageSize * 2, // Get more to filter for partners
          p_sort_column: 'created_at',
          p_sort_direction: 'DESC'
        });

      if (usersError) {
        logger.error('Supabase error:', usersError);
        throw usersError;
      }

      if (!data || !Array.isArray(data)) {
        logger.error('Invalid response format:', data);
        throw new Error('Invalid response format');
      }

      const response = data[0];
      if (!response || !response.data || !Array.isArray(response.data)) {
        logger.error('Invalid data format:', response);
        throw new Error('Invalid data format');
      }

      // Filter for partners only
      const partnerUsers = response.data.filter((user: any) => user.role === 'partner');
      
      // Get partner-specific data for each partner
      const partnersWithDetails = await Promise.all(
        partnerUsers.map(async (partnerUser: any) => {
          try {
            // Get partner metadata from raw_user_meta_data
            const partnerDetails = partnerUser.raw_user_meta_data || {};
            
            return {
              ...partnerUser,
              partner_name: partnerDetails.partner_name || partnerUser.email,
              commission_rate: partnerDetails.commission_rate || 0.01,
              // Removed status from partner metadata - use unified is_blocked instead
              allowed_categories: partnerDetails.allowed_categories || [],
              max_markets_per_day: partnerDetails.max_markets_per_day || 50,
              markets_created_today: partnerDetails.markets_created_today || 0,
              total_markets_created: partnerDetails.total_markets_created || 0,
              last_api_call: partnerDetails.last_api_call || null
            };
          } catch (err) {
            logger.error('Error getting partner details:', err);
            return partnerUser;
          }
        })
      );

      setPartners(partnersWithDetails);
      setTotalPages(Math.ceil(partnersWithDetails.length / pageSize));
      setTotalPartners(partnersWithDetails.length);
      setError(null);
    } catch (err) {
      logger.error('Error in fetchPartners:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Failed to load partners. Please ensure you have the correct permissions.'
      );
      setPartners([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  useEffect(() => {
    fetchPartners(true);
  }, []);

  useEffect(() => {
    fetchPartners(false);
  }, [debouncedSearchTerm, filterStatus, currentPage]);

  const handleStatusToggle = async (partnerId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase.rpc('admin_toggle_user_block', {
        target_user_id: partnerId,
        block_status: !currentStatus
      });

      if (error) throw error;

      setActionMessage({
        type: 'success',
        text: `Partner ${currentStatus ? 'unblocked' : 'blocked'} successfully`
      });

      // Refresh the list
      fetchPartners(false);
    } catch (err) {
      logger.error('Error toggling partner status:', err);
      setActionMessage({
        type: 'error',
        text: 'Failed to update partner status'
      });
    }

    // Clear message after 3 seconds
    setTimeout(() => setActionMessage(null), 3000);
  };

  const exportPartners = () => {
    const csvData = partners.map(partner => ({
      'Partner Name': partner.partner_name || partner.email,
      'Email': partner.email,
      'Status': partner.is_blocked ? 'Blocked' : 'Active', // Unified status
      'Commission Rate': `${(partner.commission_rate || 0.01) * 100}%`,
      'Max Markets/Day': partner.max_markets_per_day || 50,
      'Markets Created Today': partner.markets_created_today || 0,
      'Total Markets Created': partner.total_markets_created || 0,
      'Wallet Balance': partner.wallet_balance ? `R ${partner.wallet_balance.toFixed(2)}` : 'N/A',
      'Last API Call': partner.last_api_call || 'Never',
      'Created': new Date(partner.created_at).toLocaleDateString(),
      'Last Login': partner.last_sign_in_at ? new Date(partner.last_sign_in_at).toLocaleDateString() : 'Never'
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `partners_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Simplified status color function (same as AdminManagement.tsx)
  const getStatusColor = (isBlocked: boolean) => {
    return isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
            <p className="ml-3 text-gray-600">Loading Partners...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/admin"
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Partner Management</h1>
                <p className="text-gray-600">Manage external partners and their API access</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={exportPartners}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowAddPartnerModal(true)}
                className="flex items-center px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-dark-teal transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Partner
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Partners</p>
                <p className="text-2xl font-bold text-gray-900">{totalPartners}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Partners</p>
                <p className="text-2xl font-bold text-gray-900">
                  {partners.filter(p => !p.is_blocked).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Markets Created</p>
                <p className="text-2xl font-bold text-gray-900">
                  {partners.reduce((sum, p) => sum + (p.total_markets_created || 0), 0)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Wallet Balance</p>
                <p className="text-2xl font-bold text-gray-900">
                  R {partners.reduce((sum, p) => sum + (p.wallet_balance || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className={`mb-6 p-4 rounded-md ${
            actionMessage.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {actionMessage.text}
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search partners by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              />
              {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-5 w-5 border-b-2 border-wayja-teal"></div>}
            </div>
            <div className="flex items-center">
              <Filter className="text-gray-400 w-5 h-5 mr-2" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
              >
                <option value="all">All Partners</option>
                <option value="active">Active Partners</option>
                <option value="blocked">Blocked Partners</option>
              </select>
            </div>
          </div>
        </div>

        {/* Partners Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Markets</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wallet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {partners && partners.length > 0 ? (
                  partners.map((partner) => (
                    <tr key={partner.id} className={partner.is_blocked ? "bg-red-50" : ""}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-wayja-teal flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {(partner.partner_name || partner.email).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {partner.partner_name || partner.email}
                            </div>
                            <div className="text-sm text-gray-500">{partner.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(partner.is_blocked)}`}>
                          {partner.is_blocked ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {((partner.commission_rate || 0.01) * 100).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {partner.markets_created_today || 0} today
                        </div>
                        <div className="text-sm text-gray-500">
                          {partner.total_markets_created || 0} total
                        </div>
                        <div className="text-xs text-gray-400">
                          Limit: {partner.max_markets_per_day || 50}/day
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {partner.wallet_balance !== null 
                          ? `R ${partner.wallet_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                          : 'N/A'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          {partner.last_api_call 
                            ? new Date(partner.last_api_call).toLocaleDateString()
                            : 'Never'
                          }
                        </div>
                        <div className="text-xs text-gray-400">
                          {partner.last_sign_in_at 
                            ? `Login: ${new Date(partner.last_sign_in_at).toLocaleDateString()}`
                            : 'No login'
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleStatusToggle(partner.id, partner.is_blocked)}
                            className={`px-3 py-1 rounded-md text-xs font-medium ${
                              partner.is_blocked
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {partner.is_blocked ? 'Unblock' : 'Block'}
                          </button>
                          <button
                            onClick={() => setShowPartnerManagementModal(partner)}
                            className="px-3 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => setShowApiKeysModal({ 
                              partnerName: partner.partner_name || partner.email,
                              partnerId: partner.id 
                            })}
                            className="p-2 rounded-full hover:bg-gray-100"
                            title="API Keys"
                          >
                            <Key className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      {error ? (
                        <div>
                          <p className="text-red-600 mb-2">Error: {error}</p>
                          <button
                            onClick={() => fetchPartners(true)}
                            className="text-wayja-teal hover:underline"
                          >
                            Try again
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p>No partners found</p>
                          {debouncedSearchTerm && (
                            <p className="text-sm mt-1">Try adjusting your search terms</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalPartners)}
                    </span>{' '}
                    of <span className="font-medium">{totalPartners}</span> partners
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {showAddPartnerModal && (
          <AddPartnerModal
            onClose={() => setShowAddPartnerModal(false)}
            onAdd={() => {
              setShowAddPartnerModal(false);
              fetchPartners(false);
            }}
          />
        )}

        {showApiKeysModal && (
          <PartnerApiKeysModal
            partnerName={showApiKeysModal.partnerName}
            partnerId={showApiKeysModal.partnerId}
            onClose={() => setShowApiKeysModal(null)}
          />
        )}

        {showPartnerManagementModal && (
          <PartnerManagementModal
            partner={showPartnerManagementModal}
            onClose={() => setShowPartnerManagementModal(null)}
            onUpdate={() => {
              setShowPartnerManagementModal(null);
              fetchPartners(false);
            }}
          />
        )}
      </div>
    </div>
  );
} 